import Axios, { AxiosV2 } from '@services/axios';

/**
 * Enhanced API utility for feature-based architecture
 * Provides a consistent interface for making authenticated API calls
 * Uses the global Axios instance with Clerk auth interceptor
 * @param {string} version - API version to use ('v1' or 'v2'), defaults to 'v1'
 */
export const useApi = (version = 'v1') => {
  // Select the appropriate axios instance based on version
  const axiosInstance = version === 'v2' ? AxiosV2 : Axios;

  // API methods with consistent error handling and response structure
  const api = {
    // GET request
    get: async (url, config = {}) => {
      try {
        const response = await axiosInstance.get(url, config);
        return { data: response.data, error: null };
      } catch (error) {
        return { data: null, error: error.response?.data || error.message };
      }
    },

    // POST request
    post: async (url, data = {}, config = {}) => {
      try {
        const response = await axiosInstance.post(url, data, config);
        return { data: response.data, error: null };
      } catch (error) {
        return { data: null, error: error.response?.data || error.message };
      }
    },

    // PUT request
    put: async (url, data = {}, config = {}) => {
      try {
        const response = await axiosInstance.put(url, data, config);
        return { data: response.data, error: null };
      } catch (error) {
        return { data: null, error: error.response?.data || error.message };
      }
    },

    // PATCH request
    patch: async (url, data = {}, config = {}) => {
      try {
        const response = await axiosInstance.patch(url, data, config);
        return { data: response.data, error: null };
      } catch (error) {
        return { data: null, error: error.response?.data || error.message };
      }
    },

    // DELETE request
    delete: async (url, config = {}) => {
      try {
        const response = await axiosInstance.delete(url, config);
        return { data: response.data, error: null };
      } catch (error) {
        return { data: null, error: error.response?.data || error.message };
      }
    },

    // File upload helper
    upload: async (url, formData, onUploadProgress = null) => {
      try {
        const config = {
          headers: { 'Content-Type': 'multipart/form-data' },
        };
        if (onUploadProgress) {
          config.onUploadProgress = onUploadProgress;
        }
        const response = await axiosInstance.post(url, formData, config);
        return { data: response.data, error: null };
      } catch (error) {
        return { data: null, error: error.response?.data || error.message };
      }
    },

    // Raw axios instance for advanced use cases
    instance: axiosInstance,
  };

  return api;
};

/**
 * Higher-order function to create feature-specific API services
 * @param {string} baseEndpoint - Base endpoint for the feature (e.g., '/courses', '/users')
 * @param {string} version - API version to use ('v1' or 'v2'), defaults to 'v1'
 * @returns {function} Hook that returns feature-specific API methods
 */
export const createFeatureApi = (baseEndpoint, version = 'v1') => {
  return () => {
    const api = useApi(version);

    return {
      // Feature-specific methods with base endpoint prepended
      list: (params = {}) => api.get(baseEndpoint, { params }),
      get: (id) => api.get(`${baseEndpoint}/${id}`),
      create: (data) => api.post(baseEndpoint, data),
      update: (id, data) => api.put(`${baseEndpoint}/${id}`, data),
      patch: (id, data) => api.patch(`${baseEndpoint}/${id}`, data),
      delete: (id) => api.delete(`${baseEndpoint}/${id}`),

      // Access to base API for custom endpoints
      api,
    };
  };
};